import { useState } from "react";

import { useLastKnownTotal, useTableState } from "@/ds";
import {
  formatPapelObraJanela,
  isPapeisObraSortField,
  PAPEL_OBRA_FORBIDDEN_MESSAGE,
  PAPEL_OBRA_NOT_FOUND_MESSAGE,
  PAPEL_OBRA_UPDATE_FORBIDDEN_MESSAGE,
  useCreatePapelObra,
  useInactivatePapelObra,
  usePapeisObra,
  useUpdatePapelObra,
} from "@/modules/recursos";
import type { InactivatePapelObraFailure, PapelObra } from "@/modules/recursos";

import type { PapelObraFormErrors, PapelObraFormValues } from "../papel-obra-form-dialog";
import {
  PAPEIS_OBRA_CREATED_MESSAGE,
  PAPEIS_OBRA_INACTIVATED_MESSAGE,
  PAPEIS_OBRA_UPDATED_MESSAGE,
} from "./papeis-obra-dialog.constants";
import type {
  PapeisObraDialogProps,
  PapeisObraDialogViewModel,
  PapelObraLinha,
} from "./types-papeis-obra-dialog";

function toLinha(papel: PapelObra): PapelObraLinha {
  return {
    id: papel.id,
    papel,
    nome: papel.nome,
    janelaTexto: formatPapelObraJanela(papel),
    statusLabel: papel.ativo ? "Ativo" : "Inativo",
    statusSeverity: papel.ativo ? "success" : "danger",
    podeInativar: papel.ativo,
    acoesLabel: `Ações de ${papel.nome}`,
    statusTestId: `papeis-obra-status-${papel.id}`,
    menuTestId: `papeis-obra-menu-${papel.id}`,
    editarTestId: `papeis-obra-edit-${papel.id}`,
    inativarTestId: `papeis-obra-inactivate-${papel.id}`,
  };
}

/**
 * Modal "Definir Papel na Obra": busca, "Novo papel" e a listagem paginada e ordenada no servidor (HU 2033).
 * "Janela de alocação" não é ordenável (decisão da PO, 25/09). O ⋮ da linha abre a edição (HU 2035, com os dados da
 * própria linha, sem GET) e a inativação (HU 2037).
 */
export function usePapeisObraDialog({
  onHide,
  onNotify,
}: PapeisObraDialogProps): PapeisObraDialogViewModel {
  const [novoAberto, setNovoAberto] = useState(false);
  const [search, setSearch] = useState("");
  const [editando, setEditando] = useState<PapelObra | null>(null);
  const [inativando, setInativando] = useState<PapelObra | null>(null);
  const { create } = useCreatePapelObra();
  const { update } = useUpdatePapelObra();
  const { inactivate } = useInactivatePapelObra();

  // Erro e carregamento zeram o total da lista: a página só volta à 1ª com um total real menor.
  const { lastTotal, remember } = useLastKnownTotal();
  const table = useTableState({
    isSortField: isPapeisObraSortField,
    total: lastTotal,
  });

  const lista = usePapeisObra({
    search,
    page: table.page + 1,
    pageSize: table.rows,
    sort: table.sort,
  });
  remember(lista.status, lista.total);

  const hasSearch = search.trim() !== "";

  /** Nunca rejeita: devolve os erros por campo para o formulário exibir. */
  async function cadastrar(values: PapelObraFormValues): Promise<PapelObraFormErrors | void> {
    const result = await create(values);

    if (result.ok) {
      setNovoAberto(false);
      lista.reload();
      onNotify({ severity: "success", summary: "Sucesso", detail: PAPEIS_OBRA_CREATED_MESSAGE });
      return;
    }

    if (result.forbidden) {
      onNotify({
        severity: "error",
        summary: "Acesso não autorizado",
        detail: PAPEL_OBRA_FORBIDDEN_MESSAGE,
      });
      return;
    }

    if (result.message) onNotify({ severity: "error", summary: result.message });

    return { nameError: result.nameError, janelaError: result.janelaError };
  }

  /** Nunca rejeita: devolve os erros por campo para o formulário exibir. */
  async function salvarEdicao(values: PapelObraFormValues): Promise<PapelObraFormErrors | void> {
    if (!editando) return;

    const result = await update({ id: editando.id, ...values });

    if (result.ok) {
      setEditando(null);
      lista.reload();
      onNotify({ severity: "success", summary: "Sucesso", detail: PAPEIS_OBRA_UPDATED_MESSAGE });
      return;
    }

    if (result.forbidden) {
      onNotify({
        severity: "error",
        summary: "Acesso não autorizado",
        detail: PAPEL_OBRA_UPDATE_FORBIDDEN_MESSAGE,
      });
      return;
    }

    if (result.notFound) {
      setEditando(null);
      lista.reload();
      onNotify({ severity: "error", summary: PAPEL_OBRA_NOT_FOUND_MESSAGE });
      return;
    }

    if (result.message) onNotify({ severity: "error", summary: result.message });

    return { nameError: result.nameError, janelaError: result.janelaError };
  }

  /** Nunca rejeita: no sucesso fecha e recarrega; na recusa devolve o motivo ao modal. */
  async function confirmarInativacao(
    justificativa: string,
  ): Promise<InactivatePapelObraFailure | void> {
    if (!inativando) return;

    const result = await inactivate({ id: inativando.id, justificativa });

    if (result.ok) {
      setInativando(null);
      lista.reload();
      onNotify({
        severity: "success",
        summary: "Sucesso",
        detail: PAPEIS_OBRA_INACTIVATED_MESSAGE,
      });
      return;
    }

    // O papel não existe mais: a listagem já está desatualizada.
    if (result.kind === "notFound") lista.reload();

    return result;
  }

  return {
    search,
    buscar: (texto) => {
      setSearch(texto);
      table.resetPage();
    },
    linhas: lista.papeis.map(toLinha),
    total: lista.total,
    status: lista.status,
    errorMessage: lista.errorMessage,
    table,
    hasSearch,
    limparBusca: () => {
      setSearch("");
      table.resetPage();
    },
    tentarNovamente: lista.reload,
    fechar: onHide,
    novoPapel: () => setNovoAberto(true),
    editar: (linha) => setEditando(linha.papel),
    inativar: (linha) => setInativando(linha.papel),
    cadastro: novoAberto ? { onSubmit: cadastrar, onCancel: () => setNovoAberto(false) } : null,
    edicao: editando
      ? { papel: editando, onSubmit: salvarEdicao, onCancel: () => setEditando(null) }
      : null,
    inativacao: inativando
      ? {
          papel: inativando,
          onConfirm: confirmarInativacao,
          onCancel: () => setInativando(null),
        }
      : null,
  };
}
